/**
 * Lottery header (§4.2 A above the fold): organizer mark + presenting-sponsor lockup (§7.1 HERO_LOCKUP) +
 * the quiet Preferences control. Sponsor placements never cover the CTA; impressions fire via the kit's
 * viewability rule and are reported as `sponsor_impression` (SPONSOR consent class).
 */
import { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router';
import { SlidersHorizontal } from 'lucide-react';
import type { PublicLotteryDto } from '@windrop/contracts';
import { Button, SponsorLockup, useIntl, type SponsorRef } from '@windrop/ui';
import { track } from '../lib/analytics';
import { titleSponsor } from '../lib/theme';

// Lazy: the sheet (Radix dialog + switches) is only downloaded when a participant opens it (bundle budget).
const PreferencesSheet = lazy(() =>
  import('./PreferencesSheet').then((m) => ({ default: m.PreferencesSheet })),
);

export function sponsorRef(s: PublicLotteryDto['sponsors'][number]): SponsorRef {
  return {
    id: s.sponsorId,
    name: s.name,
    logoUrl: s.logoLightUrl ?? s.logoDarkUrl ?? '',
    logoUrlDark: s.logoDarkUrl,
    tier: s.tier,
    href: s.websiteUrl,
    tagline: s.tagline,
  };
}

export function LotteryHeader({ lottery }: { lottery: PublicLotteryDto }) {
  const { t } = useIntl();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefsRequested, setPrefsRequested] = useState(false);
  const sponsor = titleSponsor(lottery);
  const showLockup =
    sponsor &&
    lottery.presentation.sponsorPlacements.includes('HERO_LOCKUP') &&
    sponsor.placements.includes('HERO_LOCKUP');
  return (
    <header className="lottery-header">
      <div className="lottery-header__row">
        <Link to={`/l/${lottery.slug}`} className="organizer-mark" aria-label={lottery.organizer.name}>
          {lottery.organizer.logoUrl ? (
            <img src={lottery.organizer.logoUrl} alt="" width={32} height={32} decoding="async" />
          ) : null}
          <span>{lottery.organizer.name}</span>
        </Link>
        <Button
          variant="quiet"
          icon={<SlidersHorizontal size={18} aria-hidden="true" />}
          onClick={() => {
            setPrefsRequested(true);
            setPrefsOpen(true);
          }}
          aria-haspopup="dialog"
          data-testid="preferences-button"
        >
          {t('shell.preferences', {}, 'Preferences')}
        </Button>
      </div>
      {showLockup && sponsor ? (
        <SponsorLockup
          sponsor={sponsorRef(sponsor)}
          label={t('shell.presentedBy', {}, 'Presented by')}
          placementId="HERO_LOCKUP"
          onImpression={(p) =>
            track(
              'sponsor_impression',
              { sponsorId: p.sponsorId, placement: 'HERO_LOCKUP', viewable: true, click: false },
              { placement: 'HERO_LOCKUP' },
            )
          }
          onClick={(p) =>
            track(
              'sponsor_impression',
              { sponsorId: p.sponsorId, placement: 'HERO_LOCKUP', viewable: true, click: true },
              { placement: 'HERO_LOCKUP' },
            )
          }
        />
      ) : null}
      {prefsRequested ? (
        <Suspense fallback={null}>
          <PreferencesSheet open={prefsOpen} onOpenChange={setPrefsOpen} />
        </Suspense>
      ) : null}
    </header>
  );
}
